import React, { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Flex,
  Menu,
  notification,
  Popconfirm,
  Radio,
  Row,
  Table,
  Tooltip,
} from 'antd'
import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import MDEditor from '@uiw/react-md-editor'
import { isEmpty } from 'lodash'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Can,
  DragAndDrop,
  DragButton,
  Icon,
  PageContainer,
  PageTitleRow,
} from '../../components'
import {
  handleBackendError,
} from '../../utils'
import { IconContainer } from '../../elements'
import { getCountryList } from '../country-management/api'
import FaqDetailsModal from './FaqDetailsModal'
import { getFaqCategories } from '../faq-categories/api'
import {
  createFaq,
  deleteFaq,
  getFaqs,
  updateFaq,
} from './api'

const Faq = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [categoryFilters, setCategoryFilters] = useState(false)
  const [isModalVisible, setModalVisibility] = useState(false)
  const [isModalLoading, setModalLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState()
  const [faqs, setFaqs] = useState([])
  const [countries, setCountries] = useState([])
  const [defaultCountryId, setDefaultCountryId] = useState(null)
  const [selectedFaq, setSelectedFaq] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const items = countries.map( country => (
    {
      key: country.id,
      label: (<span>{country.name}</span>),
    }
  ))

  const [metaData, setMetaData] = useState()

  useEffect(() => {
    if (isEmpty(searchParams.toString()))
      setSearchParams({ items_per_page: 25, page: 1 })
  }, [])

  useEffect(() => {
    getCountryList()
      .then(({ data }) => {
        const tempParams = { ...searchParams }
        const defaultCountry =
          data.data.find(country => country.code === 'fr') ||
          data.data.find(country => country.name === 'France') ||
          data.data[0]
        const defaultCountryId = defaultCountry?.id

        setCountries(data.data)
        setDefaultCountryId(defaultCountryId)

        setSearchParams({
          ...tempParams,
          category_type: searchParams.get('category_type') || 'regular',
          country_id:  searchParams.get('country_id') || defaultCountryId,
        })
      })
      .catch((error) => {
        handleFailure(error, 'Fetching countries failed!')
      })
  }, [])

  useEffect(() => {
    if (!isEmpty(searchParams.toString())) {
      fetchFaqs(searchParams.toString())
      fetchCategories(`category_type=${searchParams.get('category_type') || 'regular'}&country_id=${searchParams.get('country_id') || defaultCountryId}`)
    }
  }, [searchParams])

  const fetchFaqs = (params) => {
    setIsLoading(true)
    getFaqs(params)
      .then(({ data }) => {
        setFaqs(data.data)
        setMetaData(data.meta)
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)
        handleFailure(error, 'Fetching FAQs failed!')
      })
  }

  const fetchCategories = (params) => {
    getFaqCategories(params)
      .then(({ data }) => setCategoryFilters(() => data.data.map(({ id, title }) => ({ text: title, value: id }))))
      .catch((error) => handleFailure(error, 'Fetching categories failed!'))
  }

  const tableColumns = [
    {
      align: 'center',
      className: 'positionSelenium',
      dataIndex: 'position',
      key: 'position',
      render: () => <DragButton />,
      width: 80,
    },
    {
      className: 'categorySelenium',
      dataIndex: 'Category',
      render: (_, record) => (
        <p>{record.category_title}</p>
      ),
      title: 'Category',
      ...getTableFilter({ filterValues: categoryFilters, isMultiple: false, name: 'faq_category_id_eq', type: 'checkbox' }),
    },
    {
      className: 'questionSelenium',
      key: 'question',
      render: (_, record) => (
        <p>{record.question[Object.keys(record.question)[0]]}</p>
      ),
      title: 'Question',
    },
    {
      className: 'answerSelenium',
      key: 'answer',
      render: (_, record) => (
        <MDEditor.Markdown
          source={record.answer[Object.keys(record.answer)[0]]}
          style={{ backgroundColor: 'transparent' }}
        />
      ),
      title: 'Answer',
    },
    {
      className: 'faqActionSelenium',
      key: 'action',
      render: (_, record) => (
        <IconContainer>
          <Can
            no={() => (
              <Tooltip title="View">
                <Icon
                  data-testid="updateFaqSelenium"
                  name="updateFaqSelenium"
                  onClick={() => handleOpenModal(record)}
                  type="eye"
                />
              </Tooltip>
            )}
            requiredPermission="faq.put.faq_id"
            yes={() => (
              <Tooltip title="Edit">
                <Icon
                  data-testid="updateFaqSelenium"
                  name="updateFaqSelenium"
                  onClick={() => handleOpenModal(record)}
                  type="edit"
                />
              </Tooltip>
            )}
          />
          <Can
            requiredPermission="faq.delete.faq_id"
            yes={() => (
              <Tooltip title="Delete">
                <Popconfirm
                  cancelText="No"
                  okText="Yes"
                  onConfirm={() => handleDeleteFaq(record.id)}
                  title="Are you sure you want to delete this faq?"
                >
                  <Icon data-testid="deleteFaqSelenium" name="deleteFaqSelenium" type="delete" />
                </Popconfirm>
              </Tooltip>
            )}
          />
        </IconContainer>
      ),
      title: 'Actions',
    },
  ]

  const onTypeChange = (event) => {
    setSearchParams({ category_type: event.target.value, country_id: searchParams.get('country_id') || defaultCountryId, items_per_page: 25, page: 1 })
  }

  const handleClickCountry = ({ key }) => {
    setSearchParams({ category_type: searchParams.get('category_type') || 'regular', country_id: key, items_per_page: 25, page: 1 })
  }

  const handleDeleteFaq = (recordId) => {
    deleteFaq(recordId)
      .then(() => handleSuccess({ description: 'Faq removed' }))
      .catch((error) => handleFailure(error, 'Faq removal failed!'),
      )
  }

  const handleFormSubmit = (formData, id) => (
    (id)
      ? (
        updateFaq(formData, id)
          .then(() => handleSuccess({ description: 'Faq updated' }))
          .catch((error) => handleFailure(error, 'Faq update failed!'),
          )
      )
      : (
        createFaq(formData)
          .then(() => handleSuccess({ description: 'Faq created' }))
          .catch((error) => handleFailure(error, 'Faq creation failed!'),
          )
      )
  )

  const handleOpenModal = (selectedFaq) => {
    setSelectedFaq(selectedFaq)
    setErrorMessage(null)
    setModalVisibility(true)
    setModalLoading(false)
  }

  const handleSuccess = (messageOptions) => {
    fetchFaqs(searchParams.toString())
    setErrorMessage(null)
    setModalVisibility(false)

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }

    const errorMessage = handleBackendError(error, message)

    setErrorMessage(errorMessage)
    setModalLoading(false)

    notification.error({
      description: errorMessage,
      title: 'Error',
    })
  }

  const setSearchParamsWithPersist = (params) => {
    const next = new URLSearchParams(params)
    next.set('country_id', searchParams.get('country_id') || defaultCountryId)
    next.set('category_type', searchParams.get('category_type') || 'regular')
    setSearchParams(next)
  }

  const onDragEnd = ({ active, over }) => {
    if (active.id === over?.id) return false

    setIsLoading(true)

    updateFaq({ position: over.data.current.position }, active.id)
      .then(() => fetchFaqs(searchParams.toString()))
      .catch((error) => handleFailure(error, 'Category update failed!'))
  }

  return (
    <PageContainer data-color-mode="light">
      <PageTitleRow
        title="FAQs"
        titleChildren={
          <Flex
            gap="middle"
            vertical
          >
            <b>FAQ Type</b>
            <Radio.Group buttonStyle="solid" defaultValue={searchParams.get('category_type') || 'regular'} onChange={onTypeChange}>
              <Radio.Button value="regular">Regular</Radio.Button>
              <Radio.Button value="corporate">Corporate</Radio.Button>
            </Radio.Group>
          </Flex>
        }
      >
        <Col
          style={{
            alignItems: 'center',
            color:'#1890ff',
            display: 'flex',
            fontWeight: 'bold',
            justifyContent: 'flex-end',
            marginBottom: '15px',
          }}
        >
          <Icon type="partition" />
          <Link to={`/faq_categories?category_type=${searchParams.get('category_type') || 'regular'}&country_id=${searchParams.get('country_id') || defaultCountryId}`}>Edit or create new category</Link>
          <Icon type="arrowRight" />
        </Col>
      </PageTitleRow>

      <Row>

        <Col span={20}>
          {countries &&
            <Menu data-testid="menu" items={items} mode="horizontal" onClick={handleClickCountry} selectedKeys={[searchParams.get('country_id')]} />
          }
        </Col>
        <Col span={4} style={{ borderBottom: '1px solid #f0f0f0', textAlign:'right' }}>
          <Can
            requiredPermission="faq.post.create"
            yes={() => (
              <Button
                data-testid="addNewFaqSelenium"
                name="addNewFaqSelenium"
                onClick={() => handleOpenModal(null)}
                type="primary"
              >
                NEW FAQ
              </Button>
            )}
          />
        </Col>
        <Col span={24}>
          <DragAndDrop items={faqs.map(({ id }) => id)} onDragEnd={onDragEnd}>
            <Table
              className="faqPageTableSelenium"
              columns={tableColumns}
              data-testid="faqPageTableSelenium"
              dataSource={faqs}
              loading={isLoading}
              onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParamsWithPersist)}
              pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
              rowKey="id"
            />
          </DragAndDrop>

          {isModalVisible && (
            <FaqDetailsModal
              countries={countries}
              errorMessage={errorMessage}
              hideModal={() => setModalVisibility(false)}
              isModalLoading={isModalLoading}
              onDelete={handleDeleteFaq}
              onSubmit={handleFormSubmit}
              selectedCountryId={searchParams.get('country_id')}
              selectedFaq={selectedFaq}
              selectedFaqCategoryType={searchParams.get('category_type')}
              setModalLoading={setModalLoading}
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

export default Faq
